import { HlsVideoPlayer } from '../../../component/workout/hls-video-player'
import { MusicSvgIcon } from '../../../component/util/images'
import React, { useCallback, useEffect, useRef } from 'react'
import { BackArrow } from 'src/component/programmes/programme-back-arrow'
import { Button, Container } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { useFeaturedWorkouts } from 'src/hook/use-featured-workouts'
import { featuredAnalytics } from 'src/service/featured-analytics-service'

/** Same threshold the authed player uses to call a workout finished. */
const VIDEO_COMPLETED_PERCENT = 0.75

/**
 * Plays a featured workout for a **signed-out visitor**.
 *
 * This is not `component/workout/workout-video.tsx`, and cannot be: that component reads
 * `SessionContext` and drives completion mutations against the account. A guest has no
 * account, so there is nothing to record a completion against — F-23's "recorded as a normal
 * play" has no server-side meaning here. What it does share is the player itself
 * (`HlsVideoPlayer`) with the full native control set, so a guest gets the same playback
 * experience, not a cut-down teaser.
 *
 * The workout comes from the already-cached featured query rather than `GET /workouts/:id`,
 * which is guarded — a guest cannot call it. That also means no extra request: the shelf they
 * clicked from populated the cache.
 */
export const FeaturedWorkoutPlayerScreen = (): React.ReactElement => {
  const { workoutId } = useParams<{ workoutId: string }>()
  const navigate = useNavigate()
  const { featuredWorkouts, isLoading } = useFeaturedWorkouts()

  const workout = featuredWorkouts.find(({ id }) => id === workoutId)
  const playerRef = useRef<HTMLVideoElement>(null)
  const hasStartedRef = useRef(false)
  const hasCompletedRef = useRef(false)

  /**
   * Last known position and which workout it was, so the ENDED event on unmount can report
   * both. Held in refs because that effect must have no dependencies — anything it closed over
   * would either be stale or make it re-fire mid-play.
   */
  const progressRef = useRef({ seconds: 0, percent: 0 })
  const playedRef = useRef<{ id: string; title: string }>()

  /**
   * A guest play emits the FEATURED_* events and **not** `WORKOUT_STARTED` /
   * `WORKOUT_COMPLETED`.
   *
   * Those two mean "an account played something", and there is no account here — nothing is
   * written server-side for a guest, so emitting them would put rows into a series every other
   * dashboard reads as account activity.
   *
   * `guestPlayStarted` also *remembers* the play in `localStorage`, which is how the
   * account they may later create ends up carrying `featuredPlayedAsGuest`. That property is
   * the entire guest→account join — there is no `alias` and no `$merge`.
   */
  const handlePlay = useCallback((): void => {
    if (hasStartedRef.current || !workout) return
    hasStartedRef.current = true
    playedRef.current = { id: workout.id, title: workout.title }
    featuredAnalytics.guestPlayStarted({ workoutId: workout.id, workoutTitle: workout.title })
  }, [workout])

  const handleTimeUpdate = useCallback((): void => {
    const video = playerRef.current
    if (!workout || !video) return

    const { currentTime, duration } = video
    if (!duration) return
    progressRef.current = { seconds: Math.round(currentTime), percent: Math.round((currentTime / duration) * 100) }

    if (hasCompletedRef.current || currentTime < duration * VIDEO_COMPLETED_PERCENT) return

    hasCompletedRef.current = true
    featuredAnalytics.guestPlayCompleted({ workoutId: workout.id, workoutTitle: workout.title })
  }, [workout])

  /**
   * Playback failing is invisible otherwise, and this path is the one most likely to fail:
   * a guest is handed an unsigned HLS URL with no credentials, so a dead manifest or a 403
   * shows up as plays quietly not happening. Without this you would see the number fall and
   * have nothing to look at.
   */
  const handleError = useCallback((): void => {
    if (!workout) return
    featuredAnalytics.playFailed({
      workoutId: workout.id,
      featuredErrorCode: playerRef.current?.error?.code ?? null,
      featuredStarted: hasStartedRef.current,
    })
  }, [workout])

  /**
   * Listeners go on the element, not on a wrapper: media events (`play`, `timeupdate`, `error`)
   * do not bubble, so an `onPlay` on a parent div would silently never fire.
   */
  useEffect(() => {
    const video = playerRef.current
    if (!video) return

    video.addEventListener('play', handlePlay)
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('error', handleError)
    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('error', handleError)
    }
  }, [handlePlay, handleTimeUpdate, handleError])

  /**
   * One `ENDED` row per play, carrying how far they got — which is what makes "does finishing
   * beat abandoning" answerable as a distribution rather than a single 75% boolean. Emitted on
   * teardown, so it covers leaving, navigating away and closing the tab alike; `COMPLETED`
   * stays as the clean conversion marker.
   *
   * Reads refs only, and depends on nothing, so it runs exactly once on unmount rather than
   * re-firing whenever a handler identity changes.
   */
  useEffect(
    () => () => {
      if (!hasStartedRef.current) return
      featuredAnalytics.ended({
        workoutId: playedRef.current?.id,
        workoutTitle: playedRef.current?.title,
        surface: 'sign-in',
        signedIn: false,
        featuredSecondsWatched: progressRef.current.seconds,
        featuredPercentWatched: progressRef.current.percent,
        featuredReachedCompletion: hasCompletedRef.current,
      })
    },
    [],
  )

  if (isLoading) return <></>

  // Deep-linked to a workout that is no longer free, or never was. Send them back to sign-in
  // rather than showing a broken player.
  if (!workout || !workout.videoUrl) {
    return (
      <Container className="py-10 text-center" data-testid="featured-workout-player-unavailable">
        <p className="font-inter text-lg">This workout is not available right now.</p>
        <Button variant="link" onClick={() => navigate('/auth/get-in')}>
          Back to sign in
        </Button>
      </Container>
    )
  }

  return (
    <Container className="py-6 flex flex-col gap-4" data-testid="featured-workout-player">
      {/*
       * The same back control the signed-in details page uses (`screen/workout/workout-screen.tsx`
       * renders the identical `<BackArrow />` above `WorkoutDisplay`) — arrow plus a "Back"
       * label, in its own row above the content, rather than a text link in the title row.
       *
       * `backTo` is passed rather than left to the component's `window.history.back()` default,
       * and that is deliberate: this route is reachable by URL with no history behind it (a
       * shared link, and both e2e specs navigate straight to it), where `history.back()` goes
       * nowhere. A guest's only meaningful destination is sign-in.
       *
       * The testid moves to the wrapper because `BackArrow` takes no `data-testid` prop, and
       * threading one through a component with ~15 other callers is not worth it for this.
       */}
      <div data-testid="featured-workout-player-back">
        <BackArrow backTo="/auth/get-in" />
      </div>
      <HlsVideoPlayer ref={playerRef} src={workout.videoUrl} autoPlay controls className="w-full rounded-md" />
      <div className="flex items-center justify-between gap-4">
        {/*
         * `min-w-0` lets a long title shrink and wrap instead of forcing the row wider than the
         * container: the `h1` is `text-2xl sm:text-4xl`, and at 390px a two-word Evogria title
         * already fills the line, so without this the playlist icon gets pushed off the edge.
         */}
        <h1 className="font-evogria uppercase text-2xl sm:text-4xl m-0 min-w-0 break-words">{workout.title}</h1>
        {/*
         * `MusicSvgIcon` and not the programme popup's `ProgrammeSpotifySvgIcon`: this is the
         * same glyph the signed-in *video page* uses, so the same action does not get two
         * different icons either side of sign-in (design.md Decision 1).
         *
         * Rendered on presence — a ternary, never `&&`. `target="_blank"` is load-bearing
         * rather than incidental here: a guest activating this is mid-workout, so the
         * playlist must not navigate the player away.
         */}
        {workout.playlistUrl ? (
          <a
            href={workout.playlistUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={`Open the music playlist for ${workout.title}`}
            data-testid="featured-workout-player-playlist"
            className="flex items-center shrink-0"
          >
            <MusicSvgIcon />
          </a>
        ) : null}
      </div>
    </Container>
  )
}
